import {random, round2, round2Str} from "../helpers/helpers";
import { vibrationRegistry } from "./vibrationRegistry";

// --------------------
// Date helpers
// --------------------
const pad = (v) => String(v).padStart(2, "0");

const formatDate = (d) =>
    `${pad(d.getDate())}-${d.toLocaleString("default", {
        month: "short",
    })}-${String(d.getFullYear()).slice(-2)}`;

const formatTime = (d) => `${pad(d.getHours())}:${pad(d.getMinutes())}`;

const formatDateTime = (d) => `${formatDate(d)} ${formatTime(d)}`;

// --------------------
// MAIN FUNCTION
// --------------------
export const generateVibrationGetLatestDataDemo = ({ meter_id }) => {
    let motor;

    vibrationRegistry.areas.forEach((a) =>
        a.machines.forEach((m) =>
            m.motors.forEach((mo) => {
                if (mo.id === Number(meter_id)) motor = mo;
            })
        )
    );

    if (!motor) return {};

    const now = new Date();
    const today = formatDate(now);

    // =============================
    // CURRENT VALUES (LIVE)
    // =============================
    const velocityBase = random(
        motor.limits.normal * 0.55,
        motor.limits.normal * 0.85
    );

    const velocity1 = velocityBase;
    const velocity2 = velocityBase + random(-0.2, 0.2);
    const velocity3 = velocityBase + random(-0.25, 0.25);

    const accBase = velocityBase * random(0.25, 0.45);

    const acc1 = accBase;
    const acc2 = accBase * random(0.9, 1.1);
    const acc3 = accBase * random(1.0, 1.3);

    const temperature = random(
        motor.limits.temp_normal - 4,
        motor.limits.temp_normal + 6
    );

    // =============================
    // DAILY CALCULATIONS
    // =============================
    const dMinVel = velocityBase - random(0.6, 1.1);
    const dMaxVel = velocityBase + random(0.8, 1.5);
    const dAvgVel = (dMinVel + dMaxVel) / 2;

    const dMinAcc = accBase - random(0.2, 0.4);
    const dMaxAcc = accBase + random(0.3, 0.7);
    const dAvgAcc = (dMinAcc + dMaxAcc) / 2;

    const dMinTemp = temperature - random(1.5, 3);
    const dMaxTemp = temperature + random(1.5, 3);

    // =============================
    // MONTHLY CALCULATIONS
    // =============================
    const mMinVel = dMinVel - random(0.5, 1.2);
    const mMaxVel = dMaxVel + random(1.0, 2.0);
    const mAvgVel = (mMinVel + mMaxVel) / 2;

    const mMinAcc = dMinAcc - random(0.3, 0.6);
    const mMaxAcc = dMaxAcc + random(0.6, 1.0);
    const mAvgAcc = (mMinAcc + mMaxAcc) / 2;

    const mMinTemp = temperature - random(3, 6);
    const mMaxTemp = temperature + random(4, 8);

    // =============================
    // FINAL RESPONSE (ALL KEYS)
    // =============================
    return {
        // -------- CURRENT --------
        acceleration_axis1: round2(acc1),
        acceleration_axis2: round2(acc2),
        acceleration_axis3: round2(acc3),

        velocity_axis1: round2(velocity1),
        velocity_axis2: round2(velocity2),
        velocity_axis3: round2(velocity3),

        temperature: round2(temperature),
        capacity: motor.capacity,
        status: "ON",
        last_seen: formatDateTime(now),

        // -------- DAILY VELOCITY --------
        daily_min_axis1: round2Str(dMinVel),
        daily_min_axis1_date: today,
        daily_max_axis1: round2Str(dMaxVel),
        daily_max_axis1_date: today,
        daily_avg_axis1: round2Str(dAvgVel),

        daily_min_axis2: round2Str(dMinVel * random(0.95, 1.05)),
        daily_min_axis2_date: today,
        daily_max_axis2: round2Str(dMaxVel * random(0.95, 1.05)),
        daily_max_axis2_date: today,
        daily_avg_axis2: round2Str(dAvgVel),

        daily_min_axis3: round2Str(dMinVel * random(0.9, 1.1)),
        daily_min_axis3_date: today,
        daily_max_axis3: round2Str(dMaxVel * random(0.9, 1.1)),
        daily_max_axis3_date: today,
        daily_avg_axis3: round2Str(dAvgVel),

        // -------- DAILY ACC --------
        daily_acc_min_axis1: round2Str(dMinAcc),
        daily_acc_min_axis1_date: today,
        daily_acc_max_axis1: round2Str(dMaxAcc),
        daily_acc_max_axis1_date: today,
        daily_acc_avg_axis1: round2Str(dAvgAcc),

        daily_acc_min_axis2: round2Str(dMinAcc * random(0.9, 1.1)),
        daily_acc_min_axis2_date: today,
        daily_acc_max_axis2: round2Str(dMaxAcc * random(0.9, 1.1)),
        daily_acc_max_axis2_date: today,
        daily_acc_avg_axis2: round2Str(dAvgAcc),

        daily_acc_min_axis3: round2Str(dMinAcc * random(1.0, 1.3)),
        daily_acc_min_axis3_date: today,
        daily_acc_max_axis3: round2Str(dMaxAcc * random(1.0, 1.3)),
        daily_acc_max_axis3_date: today,
        daily_acc_avg_axis3: round2Str(dAvgAcc),

        // -------- DAILY TEMP --------
        daily_min_temp: round2Str(dMinTemp),
        daily_min_temp_date: today,
        daily_max_temp: round2Str(dMaxTemp),
        daily_max_temp_date: today,
        daily_avg_temp: round2Str(temperature),

        // -------- MONTHLY VELOCITY --------
        monthly_min_axis1: round2Str(mMinVel),
        monthly_min_axis1_date: today,
        monthly_max_axis1: round2Str(mMaxVel),
        monthly_max_axis1_date: today,
        monthly_avg_axis1: round2Str(mAvgVel),

        monthly_min_axis2: round2Str(mMinVel * random(0.95, 1.05)),
        monthly_min_axis2_date: today,
        monthly_max_axis2: round2Str(mMaxVel * random(0.95, 1.05)),
        monthly_max_axis2_date: today,
        monthly_avg_axis2: round2Str(mAvgVel),

        monthly_min_axis3: round2Str(mMinVel * random(0.9, 1.1)),
        monthly_min_axis3_date: today,
        monthly_max_axis3: round2Str(mMaxVel * random(0.9, 1.1)),
        monthly_max_axis3_date: today,
        monthly_avg_axis3: round2Str(mAvgVel),

        // -------- MONTHLY ACC --------
        monthly_acc_min_axis1: round2Str(mMinAcc),
        monthly_acc_min_axis1_date: today,
        monthly_acc_max_axis1: round2Str(mMaxAcc),
        monthly_acc_max_axis1_date: today,
        monthly_acc_avg_axis1: round2Str(mAvgAcc),

        monthly_acc_min_axis2: round2Str(mMinAcc * random(0.9, 1.1)),
        monthly_acc_min_axis2_date: today,
        monthly_acc_max_axis2: round2Str(mMaxAcc * random(0.9, 1.1)),
        monthly_acc_max_axis2_date: today,
        monthly_acc_avg_axis2: round2Str(mAvgAcc),

        monthly_acc_min_axis3: round2Str(mMinAcc * random(1.0, 1.3)),
        monthly_acc_min_axis3_date: today,
        monthly_acc_max_axis3: round2Str(mMaxAcc * random(1.0, 1.3)),
        monthly_acc_max_axis3_date: today,
        monthly_acc_avg_axis3: round2Str(mAvgAcc),

        // -------- MONTHLY TEMP --------
        monthly_min_temp: round2Str(mMinTemp),
        monthly_min_temp_date: today,
        monthly_max_temp: round2Str(mMaxTemp),
        monthly_max_temp_date: today,
        monthly_avg_temp: round2Str(temperature),
    };
};
